/**
 * Web Modal Component
 *
 */

import { useContext } from 'react'
import type { HTMLProps, ReactElement, ReactNode } from 'react'
import { clsx } from 'clsx'
import { findElementInChildren } from '../../../shared/component-helper'
import type { SectionProps } from '../../section/Section'
import Section from '../../section/Section'
import Space from '../../space/Space'
import ModalContext from '../ModalContext'
import H1 from '../../../elements/H1'

export type ModalHeaderProps = {
  /**
   * The content which will appear when triggering the modal/drawer.
   */
  children?: ReactNode

  /**
   * The modal/drawer title. Displays on the very top of the content.
   */
  title?: ReactNode

  /**
   * Give the h1 component a classname (maps to `dnb-modal__title`)
   */
  titleClass?: string

  /**
   * Font size of the title (maps to `dnb-h--<size>`)
   * Default: `large`
   */
  size?: 'medium' | 'large' | 'x-large' | 'xx-large'
} & Omit<SectionProps, 'children'>

export default function ModalHeader({
  title = null,
  children = null,
  titleClass = null,
  size = null,
  ref,
  ...sectionProps
}: ModalHeaderProps &
  Omit<HTMLProps<HTMLElement>, 'size' | 'title' | 'children'>) {
  const context = useContext(ModalContext)

  const customHeader = findElementInChildren(
    children,
    (cur: ReactElement) => {
      return cur.type === 'h1' || cur.type === H1
    }
  )

  const usedTitle = title || context.title
  const showTitle = !customHeader && usedTitle
  const fontSize = size || 'large'

  return (
    <Section {...sectionProps}>
      {showTitle ? (
        <Space
          id={'dnb-modal-' + context.id + '-title'}
          element="h1"
          top="zero"
          bottom="small"
          className={clsx(
            'dnb-modal__title', // for tests
            `dnb-h--${fontSize}`,
            titleClass
          )}
        >
          {usedTitle}
        </Space>
      ) : (
        <div
          aria-hidden
          tabIndex={-1}
          className="dnb-modal__focus-helper dnb-sr-only"
        >
          {/* Used to set focus when no close button is present */}
          {usedTitle}
        </div>
      )}
      <div>{children as ReactNode}</div>
    </Section>
  )
}
